import React, { type ReactNode, useState } from 'react';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, TableData } from 'src/shared/components/data-table/dataTableTypes';
import StatusFilter from 'src/app/admin/components/status-filter/StatusFilter';
import OnboardButton, { type OnboardResult } from './OnboardButton';
import type { EstateDiscrepancies } from '../findEstateDiscrepancies';

type DiscrepancyView = 'sunriseOnly' | 'sunsetOnly' | 'outOfSync';

type ViewTable = {
    label: string;
    columns: DataTableColumns;
    rows: TableData;
    emptyStateMessage: string;
};

const customerIdColumn = { columnId: 'customerId', title: 'Business Central ID', isUnique: true };
const customerNameColumn = { columnId: 'customerName', title: 'Customer name' };
const sunriseNameColumn = { columnId: 'sunriseName', title: 'Organisation name' };
const actionColumn = { columnId: 'action', title: 'Action' };

type DiscrepancyActions = {
    onOnboarded: (customerId: string, result: OnboardResult) => void;
};

const buildViews = (discrepancies: EstateDiscrepancies, { onOnboarded }: DiscrepancyActions): Record<DiscrepancyView, ViewTable> => ({
    sunsetOnly: {
        label: 'Sunset only',
        columns: [customerIdColumn, customerNameColumn, actionColumn],
        rows: discrepancies.missingInEngine.map(customer => [
            customer.customerId,
            customer.customerName,
            <OnboardButton key={customer.customerId} {...customer} onOnboarded={onOnboarded} />,
        ]),
        emptyStateMessage: 'Every Sunset customer has a Sunrise organisation.',
    },
    sunriseOnly: {
        label: 'Sunrise only',
        columns: [customerIdColumn, sunriseNameColumn],
        rows: discrepancies.engineOnly.map(organisation => [organisation.business_central_id, organisation.name]),
        emptyStateMessage: 'Every Sunrise organisation has a Sunset customer.',
    },
    outOfSync: {
        label: 'Out of sync',
        columns: [customerIdColumn, customerNameColumn, sunriseNameColumn, { columnId: 'issue', title: 'Issue' }],
        rows: [
            ...discrepancies.archivedInEngine.map(({ customer, organisation }) => [
                customer.customerId,
                customer.customerName,
                organisation.name,
                'Archived in Sunrise',
            ]),
            ...discrepancies.nameDiffers.map(({ customer, organisation }) => [
                customer.customerId,
                customer.customerName,
                organisation.name,
                'Name differs',
            ]),
        ],
        emptyStateMessage: 'No customer and organisation disagree.',
    },
});

type DiscrepancyTableProps = DiscrepancyActions & {
    discrepancies: EstateDiscrepancies;
    sunsetOnlyActions: ReactNode;
};

const DiscrepancyTable = ({ discrepancies, sunsetOnlyActions, ...actions }: DiscrepancyTableProps) => {
    const [view, setView] = useState<DiscrepancyView>('sunsetOnly');
    const views = buildViews(discrepancies, actions);
    const selected = views[view];

    const options = (Object.keys(views) as DiscrepancyView[]).map(value => ({
        value,
        label: `${views[value].label} (${views[value].rows.length})`,
    }));

    return (
        <section className='flex flex-col gap-4'>
            <div className='flex items-stretch justify-between gap-4'>
                <StatusFilter options={options} value={view} onChange={setView} ariaLabel='Filter discrepancies' />
                <div role='toolbar' aria-label='Discrepancy actions' className='flex items-stretch gap-2'>
                    {view === 'sunsetOnly' && sunsetOnlyActions}
                </div>
            </div>
            <DataTable columns={selected.columns} state={{ data: selected.rows }} emptyStateMessage={selected.emptyStateMessage} />
        </section>
    );
};

export default DiscrepancyTable;
